import { Component, EventEmitter, Input, OnInit, Output, signal } from '@angular/core';
import { NgxPermissionsService } from 'ngx-permissions';
import { PtCoreActions } from '@pt/pt-core';
import { Store } from '@ngrx/store';
import { IoniconsFilled, NavItem, REQUIRED_VALIDATOR_KEY } from '@pt/pt-models';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { EmailsService, OrganizationsService } from '@pt/api-client';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ValidatorGenerator } from '../../../documents/utils/validator-generator';
import { NgbDropdown } from '@ng-bootstrap/ng-bootstrap';
import { Organization } from 'it/primatoo/openapi/api/org/schemas/organization/Organization';
import { PrimatooModule } from 'it/primatoo/openapi/api/org/schemas/common/PrimatooModule';
import { GetTicketInfoResponse } from 'it/primatoo/openapi/api/org/schemas/organization/GetTicketInfoResponse';

@UntilDestroy()
@Component({
  selector: 'pt-navbar-support-item',
  template: `
    <button type='button'
            class='btn w-100 text-start item'
            (click)='clickItem()' [ngClass]='item?.classCss'
            [disabled]='!isEnabled || (item?.disabled | async)'
            *ngIf='item && !item?.children else BUTTON_DROPDOWN_MENU'>
      <pt-icon *ngIf='item.icon'
               [key]='item.icon.key'
               [size]="item.icon.size"
               [iconType]="item.icon.type"
      ></pt-icon>
      {{ item.label | async }}
    </button>

    <ng-template #BUTTON_DROPDOWN_MENU>
      <div #btnDropDown="ngbDropdown" ngbDropdown display="dynamic" placement="bottom-end">
        <button ngbDropdownToggle
                class='btn w-100 text-start item'
                (click)="loadOrg()"
                [id]='item.id' [ngClass]='item?.classCss'
                [disabled]='!isEnabled || (item?.disabled | async)'>
          <pt-icon *ngIf='item.icon'
                   [key]='item.icon.key'
                   [size]="item.icon.size"
                   [iconType]="item.icon.type"
          ></pt-icon>
          {{ item.label | async }}
        </button>

        <div ngbDropdownMenu class='dropdown-menu text-center'>
          <div class="d-flex flex-column w-100 align-items-center mb-2 text-muted">
            <pt-icon [key]='"support-b.svg"' iconType="SVG" [size]='20'></pt-icon>
            <div class="my-1 fw-bold">{{ "LABELS.SUPPORT" | translate }}</div>
          </div>
          <form class="text-start px-3 w-700-px" [formGroup]="formGroup" *ngIf="!showSuccess()">
            <p>
              {{ 'MESSAGE.SUPPORT_FORM_INFO' | translate }}
            </p>
            <p>
              {{ 'MESSAGE.SUPPORT_FORM_INFO2' | translate }}
            </p>
            <ng-container *ngIf="loadingModule(); else visualize">
              <div class="w-100 text-center">
                <div class="spinner-border spinner-border-sm me-2 text-muted" role="status">
                  <span class="visually-hidden">Loading...</span>
                </div>
              </div>
            </ng-container>
            <ng-template #visualize>
              <ng-container *ngIf="enabledTicketModule; else form">
                <div class="w-100 text-center mb-3">
                  <a [href]="supportInfo.url" target="_blank">Apri Segnalazione</a>
                </div>
              </ng-container>
              <ng-template #form>
                <div class="mb-3">
                  <pt-input-text
                    [controlName]='"subject"'
                    formControlName='subject'
                    [validators]="[REQUIRED]"
                    [label]="'LABELS.SUBJECT' | translate"
                  ></pt-input-text>
                </div>
                <div>
                  <pt-input-text-area
                    [minRow]="10"
                    [validators]="[REQUIRED]"
                    formControlName='content'
                    [label]="'LABELS.CONTENT' | translate"
                  ></pt-input-text-area>
                </div>
                <ngb-alert *ngIf='errorMessage' [type]="'warning'" [dismissible]='false' [animation]='true'>
                  {{ errorMessage | translate }}
                </ngb-alert>
                <div class="d-flex justify-content-end">
                  <pt-button-with-loader
                    [loading]='loading()'
                    [mainIcon]="IconSave"
                    [withConfirm]="true"
                    [label]="'BUTTON.SEND' | translate"
                    [disabled]='formGroup.invalid || loading()'
                    [cssClass]="'btn btn-sm btn-primary gap-1 px-3'"
                    [confirmMessage]="'MESSAGE.ARE_YOU_SURE_TO_SEND_SUPPORT_REQUEST' | translate"
                    (buttonWithLoaderClick)='onSubmitForm()'
                  ></pt-button-with-loader>
                </div>
              </ng-template>
            </ng-template>

          </form>
          <ng-container *ngIf="showSuccess()">
            <div class="w-700-px px-3 mb-3">
              <p class="py-4">{{ 'MESSAGE.SUPPORT_FORM_SUCCESS' | translate }}</p>
              <button class="btn btn-sm btn-primary px-3" (click)="onClickOK(btnDropDown)">
                {{ 'BUTTON.OK' | translate }}
              </button>
            </div>
          </ng-container>
        </div>
      </div>
    </ng-template>
  `
})
export class NavbarSupportItemComponent implements OnInit {

  @Input() item: NavItem;
  @Output() clickAction = new EventEmitter<NavItem>();

  public isEnabled: boolean;
  public formGroup: FormGroup;
  public errorMessage: string;
  public IconSave = IoniconsFilled.send;
  public loading = signal(false);
  public showSuccess = signal(false);
  public REQUIRED = ValidatorGenerator.generateWidgetValidator(REQUIRED_VALIDATOR_KEY);
  public org: Organization;
  public enabledTicketModule = false;
  public loadingModule = signal(false);
  public supportInfo?: GetTicketInfoResponse;

  constructor(
    private permissionService: NgxPermissionsService,
    private store: Store,
    private emailsService: EmailsService,
    private orgService: OrganizationsService
  ) {
    this.formGroup = new FormGroup({
      subject: new FormControl(null, [Validators.required]),
      content: new FormControl(null, [Validators.required])
    });
  }

  ngOnInit(): void {
    if (this.item) {
      this.permissionService.hasPermission(this.item.permissEnabled).then((enabled) => {
        this.isEnabled = enabled;
      });
    }

    this.formGroup?.valueChanges.subscribe(() => {
      this.errorMessage = null;
    });
  }

  clickItem() {
    if (this.item?.routerLink) {
      this.store.dispatch(PtCoreActions.navigateTo({
        route: [this.item?.routerLink],
        onBlankPage: this.item?.routerLinkOpt?.routerLinkBlank,
        external: this.item?.routerLinkOpt?.routerLinkExternal
      }));
    } else {
      this.clickAction.emit(this.item);
    }
  }

  onSubmitForm() {
    this.loading.set(true);
    const form = this.formGroup.getRawValue();
    this.emailsService.sendSupportRequest(form.subject, form.content).pipe(
      untilDestroyed(this)
    ).subscribe({
      next: () => {
        this.formGroup.reset();
        this.showSuccess.set(true);
      },
      error: (e: HttpErrorResponse) => {
        if (e.error && e.error.reason) {
          this.errorMessage = e.error.reason;
        } else {
          this.errorMessage = 'MESSAGE.ERROR_OCCURRED';
        }
        this.loading.set(false);
        this.showSuccess.set(false);
      },
      complete: () => {
        this.loading.set(false);
      }
    });
  }

  onClickOK(dropdown: NgbDropdown) {
    dropdown.close();
    this.showSuccess.set(false);
  }

  loadOrg() {
    this.loadingModule.set(true);
    this.orgService.getOrganizationSelf().subscribe((org: Organization) => {
        this.org = org;
        this.enabledTicketModule = this.org.activeModules.includes(PrimatooModule.TICKET);

        if (this.enabledTicketModule) {
          this.orgService.getSupportInfo().subscribe((info) => {
            this.supportInfo = info;
            this.loadingModule.set(false);
          }, () => {
            this.supportInfo = undefined;
            this.loadingModule.set(false);
          });
        } else {
          this.loadingModule.set(false);
        }
      }, () => {
        this.loadingModule.set(false);
        this.enabledTicketModule = false;
      }
    );
  }
}
